// import { Select } from '@amzn/awsui-components-react';
import {
  Button,
  Container,
  Grid,
  Header,
  Input,
  Select,
  Multiselect,
  MultiselectProps,
  SelectProps,
  SpaceBetween,
  ExpandableSection,
} from '@amzn/awsui-components-react-v3';
import _ from 'lodash';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Category } from '../../../containers/ContainerManagement/types';
import { getSubcategoryOptions } from '../../../utils/ContainerManagement/containerDefinitionsHelper';
import KeyValue from '../../KeyValue';
import './style.css';
import { zoneFeatureFlag, capacityFilterFlag } from '../../../utils/featureFlags';

export type SearchVariables = {
  category: string;
  subcategory: string;
  room: string;
  searchString: string;
  state: string;
  isCompliant: string;
  isEmpty: string;
  zone: string;
  type: string;
  siteIds: string[];
  parentContainerType: string;
  parentContainerName: string;
  userAlias: string;
};

export type ContainerSearchProps = {
  siteId: string;
  typeOptions: SelectProps.Option[];
  categoryOptions: SelectProps.Option[];
  categoriesMap: Map<string, Category> | null;
  roomOptions: SelectProps.Option[];
  statusOptions: SelectProps.Option[];
  complianceOptions: SelectProps.Option[];
  isEmptyOptions: SelectProps.Option[];
  zoneOptions: SelectProps.Option[];
  handleTypeChange: (type: string) => void;
  setSearchVariables: (searchVariables: SearchVariables) => void;
  handleSiteChange: (type: any) => void;
  locationOptions: MultiselectProps.Option[];
  parentContainerTypeOptions: SelectProps.Option[];
};

export default function ContainerSearch(props: ContainerSearchProps): ReactElement {
  const { t } = useTranslation();
  const {
    typeOptions,
    categoryOptions,
    categoriesMap,
    roomOptions,
    statusOptions,
    complianceOptions,
    isEmptyOptions,
    zoneOptions,
    parentContainerTypeOptions,
    locationOptions,
    handleTypeChange,
    setSearchVariables,
    handleSiteChange,
  } = props;
  const [searchString, setSearchString] = useState<string>('');
  const [parentContainerName, setParentContainerName] = useState<string>('');
  const [userAlias, setUserAlias] = useState<string>('');
  const [selectedType, setSelectedType] = useState<SelectProps.Option | null>(null);
  const [selectedParentContainerType, setSelectedParentContainerType] =
    useState<SelectProps.Option | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<SelectProps.Option | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<SelectProps.Option | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<SelectProps.Option | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<SelectProps.Option | null>(null);
  const [selectedSites, setSelectedSites] = useState<any>([
    {
      label: JSON.parse(localStorage.getItem('siteId') ?? '{"locationId": ""}').label,
      value: JSON.parse(localStorage.getItem('siteId') ?? '{"locationId": ""}').value,
      locationId: JSON.parse(localStorage.getItem('siteId') ?? '{"locationId": ""}').locationId,
    },
  ]);
  const [selectedCompliantFlag, setSelectedCompliantFlag] = useState<SelectProps.Option | null>(
    null
  );

  const [selectedIsEmptyFlag, setSelectedIsEmptyFlag] = useState<SelectProps.Option | null>(null);
  const [selectedZone, setSelectedZone] = useState<SelectProps.Option | null>(null);
  const showZoneFeature = zoneFeatureFlag();
  const containerCapacityFeature = capacityFilterFlag();

  const optionComparator = (a: SelectProps.Option, b: SelectProps.Option) => {
    const aLabel = a?.label ?? '';
    const bLabel = b?.label ?? '';
    if (aLabel > bLabel) return 1;
    if (aLabel < bLabel) return -1;
    return 0;
  };

  useEffect(() => {
    typeOptions.sort(optionComparator);
  }, [typeOptions]);

  useEffect(() => {
    parentContainerTypeOptions.sort(optionComparator);
  }, [parentContainerTypeOptions]);

  useEffect(() => {
    categoryOptions.sort(optionComparator);
  }, [categoryOptions]);

  useEffect(() => {
    roomOptions.sort(optionComparator);
  }, [roomOptions]);

  const handleClear = () => {
    setSearchString('');
    setSelectedType(null);
    setSelectedParentContainerType(null);
    setParentContainerName('');
    setUserAlias('');
    handleTypeChange('');
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedRoom(null);
    setSelectedStatus(null);
    setSelectedCompliantFlag(null);
    setSelectedIsEmptyFlag(null);
    setSelectedSites([]);
    handleSiteChange([]);
  };

  const handleSearchWithFilters = () => {
    setSearchVariables({
      searchString,
      category: selectedCategory?.value ?? '',

      // subcategory label is the name, value is containerDefinitionId, elastic search wants the name
      subcategory: selectedSubcategory?.label ?? '',

      parentContainerType: selectedParentContainerType?.value ?? '',
      parentContainerName,
      userAlias,
      room: selectedRoom?.value ?? '',
      state: selectedStatus?.value ?? '',
      isCompliant: selectedCompliantFlag?.value ?? '',
      isEmpty: selectedIsEmptyFlag?.value ?? '',
      zone: selectedZone?.value ? `*${selectedZone.value}*` : '',
      type: selectedType?.value ?? '',
      siteIds: selectedSites.length > 0 ? _.map(selectedSites, 'locationId') : [],
    });
  };

  const searchStringIsValid = () => searchString.replace(/\*/g, '').length >= 3;

  return (
    <Container header={<Header variant="h2">{t('Search containers')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        <div data-test-id="search-input-div" className="flex column">
          <Input
            className="flexGrow"
            onChange={({ detail }) => setSearchString(detail.value)}
            value={searchString}
            placeholder={t('Search')}
            type="search"
            onKeyDown={({ detail }) => {
              if (detail.keyCode === 13 && searchStringIsValid()) {
                // search on enter key press
                handleSearchWithFilters();
              }
            }}
          />
          {searchString.length > 0 && !searchStringIsValid() && (
            <small
              data-test-id="searchStringError"
              style={{
                color: 'red',
              }}>
              {t('Enter at least three characters')}
            </small>
          )}
        </div>
        <Grid
          gridDefinition={[
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
            { colspan: 3 },
          ]}>
          <div>
            <KeyValue label={t('Location')}>
              <Multiselect
                data-test-id="locationDropdown"
                selectedOptions={selectedSites}
                onChange={({ detail }) => {
                  setSelectedSites(detail.selectedOptions);
                  handleSiteChange(detail.selectedOptions);
                }}
                deselectAriaLabel={(e) => `Remove ${e.value}`}
                options={_.orderBy(locationOptions, ['label'], ['asc'])}
                placeholder="Choose Location"
                selectedAriaLabel="Selected"
                tokenLimit={2}
                i18nStrings={{
                  tokenLimitShowMore: 'Show more chosen locations',
                  tokenLimitShowFewer: 'Show fewer chosen locations',
                }}
                filteringType="auto"
                expandToViewport
              />
            </KeyValue>
          </div>
          <div>
            <KeyValue label={t('Type')}>
              <Select
                data-test-id="typeDropdown"
                selectedOption={selectedType}
                onChange={({ detail }) => {
                  setSelectedType(detail.selectedOption?.value ? detail.selectedOption : null);
                  handleTypeChange(detail.selectedOption?.value ?? '');
                  setSelectedCategory(null);
                  setSelectedSubcategory(null);
                }}
                options={[
                  {
                    label: '-',
                    value: '',
                  },
                  ...typeOptions,
                ]}
                placeholder={t('Choose type')}
                empty={t('No options')}
              />
            </KeyValue>
          </div>
          <div>
            <KeyValue label={t('Category')}>
              <Select
                data-test-id="categoryDropdown"
                selectedOption={selectedCategory}
                onChange={({ detail }) => {
                  setSelectedCategory(detail.selectedOption?.value ? detail.selectedOption : null);
                  setSelectedSubcategory(null);
                }}
                options={[
                  {
                    label: '-',
                    value: '',
                  },
                  ...categoryOptions,
                ]}
                placeholder={t('Choose category')}
                empty={t('No options')}
                disabled={selectedType === null}
              />
            </KeyValue>
          </div>
          <div>
            <KeyValue label={t('Subcategory')}>
              <Select
                data-test-id="subcategoryDropdown"
                selectedOption={selectedSubcategory}
                onChange={({ detail }) => {
                  setSelectedSubcategory(
                    detail.selectedOption?.value ? detail.selectedOption : null
                  );
                }}
                options={[
                  {
                    label: '-',
                    value: '',
                  },
                  ...getSubcategoryOptions(categoriesMap, selectedCategory),
                ]}
                placeholder={t('Choose subcategory')}
                empty={t('No options')}
                disabled={selectedType === null || selectedCategory === null}
              />
            </KeyValue>
          </div>
          <div>
            <KeyValue label={t('Room')}>
              <Select
                data-test-id="roomDropdown"
                selectedOption={selectedRoom}
                onChange={({ detail }) => {
                  setSelectedRoom(detail.selectedOption?.value ? detail.selectedOption : null);
                }}
                options={[
                  {
                    label: '-',
                    value: '',
                  },
                  ...roomOptions,
                ]}
                placeholder={t('Choose room')}
                empty={t('No options')}
                disabled={selectedSites.length > 1 || selectedSites.length === 0}
              />
            </KeyValue>
          </div>
          <div>
            <KeyValue label={t('Status')}>
              <Select
                data-test-id="statusDropdown"
                selectedOption={selectedStatus}
                onChange={({ detail }) => {
                  setSelectedStatus(detail.selectedOption?.value ? detail.selectedOption : null);
                }}
                options={[
                  {
                    label: '-',
                    value: '',
                  },
                  ...statusOptions,
                ]}
                placeholder={t('Choose status')}
                empty={t('No options')}
              />
            </KeyValue>
          </div>
          <div>
            <KeyValue label={t('Compliant')}>
              <Select
                data-test-id="compliantDropdown"
                selectedOption={selectedCompliantFlag}
                onChange={({ detail }) => {
                  setSelectedCompliantFlag(
                    detail.selectedOption?.value ? detail.selectedOption : null
                  );
                }}
                options={[
                  {
                    label: '-',
                    value: '',
                  },
                  ...complianceOptions,
                ]}
                placeholder={t('Choose compliance')}
                empty={t('No options')}
              />
            </KeyValue>
          </div>
          {showZoneFeature && (
            <div>
              <KeyValue label={t('Zone')}>
                <Select
                  data-test-id="zoneDropdown"
                  selectedOption={selectedZone}
                  onChange={({ detail }) => {
                    setSelectedZone(detail.selectedOption?.value ? detail.selectedOption : null);
                  }}
                  options={[
                    {
                      label: '-',
                      value: '',
                    },
                    ...zoneOptions,
                  ]}
                  placeholder={t('Choose zone')}
                  empty={t('No options')}
                />
              </KeyValue>
            </div>
          )}
        </Grid>
        <ExpandableSection headerText="Additional filters">
          <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
            {containerCapacityFeature && (
              <div>
                <KeyValue label={t('Container With Parts')}>
                  <Select
                    data-test-id="isEmptyDropdown"
                    selectedOption={selectedIsEmptyFlag}
                    onChange={({ detail }) => {
                      setSelectedIsEmptyFlag(
                        detail.selectedOption?.value ? detail.selectedOption : null
                      );
                    }}
                    options={[
                      {
                        label: '-',
                        value: '',
                      },
                      ...isEmptyOptions,
                    ]}
                    placeholder={t('Choose capacity')}
                    empty={t('No options')}
                  />
                </KeyValue>
              </div>
            )}
            <div>
              <KeyValue label={t('Parent Container Type')}>
                <Select
                  data-test-id="parentTypeDropdown"
                  selectedOption={selectedParentContainerType}
                  onChange={({ detail }) => {
                    setSelectedParentContainerType(
                      detail.selectedOption?.value ? detail.selectedOption : null
                    );
                  }}
                  options={[
                    {
                      label: '-',
                      value: '',
                    },
                    ...parentContainerTypeOptions,
                  ]}
                  placeholder={t('Choose parent type')}
                  empty={t('No options')}
                />
              </KeyValue>
            </div>
            <div>
              <KeyValue label={t('Parent Container Name')}>
                <Input
                  data-test-id="parentNameInputBox"
                  onChange={({ detail }) => setParentContainerName(detail.value)}
                  value={parentContainerName}
                  placeholder={t('Container name')}
                />
              </KeyValue>
            </div>
            <div>
              <KeyValue label={t('User Alias')}>
                <Input
                  data-test-id="userCustodyInputBox"
                  onChange={({ detail }) => setUserAlias(detail.value)}
                  value={userAlias}
                  placeholder={t('User with custody')}
                />
              </KeyValue>
            </div>
          </Grid>
        </ExpandableSection>
        <SpaceBetween data-test-id="footer" className="rightAlign" direction="horizontal" size="s">
          <Button variant="normal" onClick={handleClear}>
            {t('Clear')}
          </Button>
          <Button
            data-test-id="search-btn"
            variant="normal"
            onClick={handleSearchWithFilters}
            disabled={
              (selectedType === null &&
                selectedRoom === null &&
                selectedStatus === null &&
                selectedCompliantFlag === null &&
                selectedIsEmptyFlag === null &&
                selectedSites.length === 0) ||
              (searchString.length > 0 && !searchStringIsValid()) ||
              selectedSites.length === 0
            }>
            {t('Search')}
          </Button>
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
}
